import { useEffect, useMemo, useState } from "react";
import { useWebSocket } from "context/WebSocketContext";
import { normalizeLinkedVehicle } from "./linkedVehicleUtils";

/**
 * `linkedDeviceData` only changes when the gateway reports. The GPS tracker reports far more
 * often over `assetTrackerWebhook`, so those messages are merged in to keep the vehicle moving
 * between gateway messages. Whichever source is newer wins.
 */
export default function useLinkedVehicles(linkedDeviceData) {
  const { assetTrackerData } = useWebSocket() ?? {};
  const [liveValues, setLiveValues] = useState({});

  const linkedIds = useMemo(
    () => new Set((linkedDeviceData ?? []).map((link) => String(link?.device_id))),
    [linkedDeviceData]
  );

  useEffect(() => {
    if (!assetTrackerData?.data) return;
    const key = String(assetTrackerData.device_id);
    if (!linkedIds.has(key)) return;
    setLiveValues((prev) => ({
      ...prev,
      [key]: { deviceValue: assetTrackerData.data, geofence: assetTrackerData.geofence ?? null },
    }));
  }, [assetTrackerData, linkedIds]);

  return useMemo(
    () =>
      (linkedDeviceData ?? [])
        .map((link) => {
          const base = normalizeLinkedVehicle(link);
          const live = liveValues[String(link?.device_id)];
          if (!live) return base;

          const merged = normalizeLinkedVehicle({
            ...link,
            geofences: live.geofence ?? link?.geofences,
            device_value: { ...link?.device_value, ...live.deviceValue },
          });
          if (!merged) return base;
          if (!base) return merged;
          return (merged.lastUpdatedMs ?? 0) >= (base.lastUpdatedMs ?? 0) ? merged : base;
        })
        .filter(Boolean),
    [linkedDeviceData, liveValues]
  );
}
